import { FunctionComponent, JSX, RefObject } from 'react'
import moment from 'moment-timezone'
import { ChildCareOutlined, Warning } from '@mui/icons-material'
import styles from './mealHandler.module.scss'
import { useTranslation } from 'react-i18next'
import Title from '@/components/_custom/Title/title'
import { MealChild, MealChildWithoutMenu, MealHabit, MealMenu, MealTexture } from '@/types/Meal'
import Desktop from '@/modules/desktopMobile/desktop.tsx'
import Mobile from '@/modules/desktopMobile/mobile.tsx'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { renderEventForMealTable } from '@/container/planning/utils/calendarEvents.tsx'

interface MealChildrenTableProps {
    childs: MealChild[]
    childsWithoutMenu: MealChildWithoutMenu[]
    selectedSectionCount: number
    isRpeOrLaep: boolean
    containerRef?: RefObject<HTMLDivElement | null>
}

const MealChildrenTable: FunctionComponent<MealChildrenTableProps> = ({
    childs,
    childsWithoutMenu,
    selectedSectionCount,
    isRpeOrLaep,
    containerRef
}: MealChildrenTableProps) => {
    const { t } = useTranslation()
    const navigate: NavigateFunction = useNavigate()

    const sortedWithoutMenu: MealChildWithoutMenu[] =
        childsWithoutMenu.length > 0
            ? [...childsWithoutMenu].sort((a: MealChildWithoutMenu, b: MealChildWithoutMenu): number => {
                  if (a.sectionName === b.sectionName) return a.firstname.localeCompare(b.firstname)
                  return a.sectionName.localeCompare(b.sectionName)
              })
            : []

    const sortedChilds: MealChild[] = [...childs].sort((a: MealChild, b: MealChild): number => {
        if (a.sectionName === b.sectionName) {
            return moment(a.start, 'HH:mm:ss').diff(moment(b.start, 'HH:mm:ss'))
        }
        return a.sectionName.localeCompare(b.sectionName)
    })

    return (
        <div className={styles.MealContainer} ref={containerRef}>
            <div className={styles.TitleAndButton}>
                <Title text={t('childsPresent')} className={styles.Title} />
            </div>
            <TableContainer className={styles.TablePlacement}>
                <Table className={styles.Table}>
                    <TableHead className={styles.TableHeader}>
                        <TableRow>
                            <TableCell align={'center'} className={styles.CellChild}>
                                {t('child')}
                            </TableCell>
                            <Desktop>
                                {selectedSectionCount > 1 && (
                                    <TableCell className={styles.CellChild} align={'center'}>
                                        {isRpeOrLaep ? t('sector') : t('section')}
                                    </TableCell>
                                )}
                                <TableCell className={styles.CellChild} align={'center'}>
                                    {t('menus')}
                                </TableCell>
                                <TableCell className={styles.CellChild} align={'center'}>
                                    {t('textures')}
                                </TableCell>
                                <TableCell className={styles.CellChild} align={'center'}>
                                    {t('habits')}
                                </TableCell>
                            </Desktop>
                        </TableRow>
                    </TableHead>
                    {sortedWithoutMenu.map((child: MealChildWithoutMenu, index: number) => (
                        <TableBody key={'nw-' + index + '-' + child.childId}>
                            <TableRow>
                                <TableCell>
                                    <div className={styles.CellIcon}>
                                        <ChildCareOutlined
                                            onClick={() => navigate('/childs?child=' + child.childId)}
                                            className={styles.Icon}
                                        />
                                        {renderEventForMealTable(
                                            child.slotType,
                                            getChildWithoutMenuSlotInformation(child)
                                        )}
                                    </div>
                                    <Mobile>
                                        <div className={`${styles.NoMenuCell} ${styles.MarginTop}`}>
                                            <Warning className={styles.NoMenuIcon} />
                                            <span>{t('noMealMenus')}</span>
                                        </div>
                                    </Mobile>
                                </TableCell>
                                <Desktop>
                                    {selectedSectionCount > 1 && (
                                        <TableCell className={styles.CellChild} align={'center'}>
                                            {child.sectionName}
                                        </TableCell>
                                    )}
                                    <TableCell colSpan={3} align={'center'}>
                                        <div className={styles.NoMenuCell}>
                                            <Warning className={styles.NoMenuIcon} />
                                            <span>{t('noMealMenus')}</span>
                                        </div>
                                    </TableCell>
                                </Desktop>
                            </TableRow>
                        </TableBody>
                    ))}
                    {sortedChilds.map((child: MealChild, index: number) => (
                        <TableBody key={index + '-' + child.childId}>
                            <TableRow>
                                <TableCell>
                                    <div className={styles.CellIcon}>
                                        <ChildCareOutlined
                                            onClick={() => navigate('/childs?child=' + child.childId)}
                                            className={styles.Icon}
                                        />
                                        {renderEventForMealTable(child.slotType, getChildSlotInformation(child))}
                                    </div>
                                    <Mobile>
                                        <div className={`${styles.CategoryTags} ${styles.MarginTop}`}>
                                            {renderChildTagMobile(child)}
                                        </div>
                                    </Mobile>
                                </TableCell>
                                <Desktop>
                                    {selectedSectionCount > 1 && (
                                        <TableCell className={styles.CellChild} align={'center'}>
                                            {child.sectionName}
                                        </TableCell>
                                    )}
                                    {renderChildTagDesktop(child)}
                                </Desktop>
                            </TableRow>
                        </TableBody>
                    ))}
                </Table>
            </TableContainer>
        </div>
    )
}

export default MealChildrenTable

function getChildWithoutMenuSlotInformation(child: MealChildWithoutMenu): string {
    const complement: string = child.complement ? ' - ' + child.complement : ''
    return `${child.firstname}${complement} - ${moment(child.start, 'HH:mm:ss').format('HH:mm')} - ${moment(child.end, 'HH:mm:ss').format('HH:mm')}`
}

function getChildSlotInformation(child: MealChild): string {
    const complement: string = child.complement ? ' - ' + child.complement : ''
    return `${child.firstname}${complement} - ${moment(child.start, 'HH:mm:ss').format('HH:mm')} - ${moment(child.end, 'HH:mm:ss').format('HH:mm')}`
}

function renderChildTagMobile(child: MealChild): JSX.Element {
    return (
        <div className={styles.CategoryTags}>
            {child.menus.map((m: MealMenu) => (
                <span key={m.id} className={styles.TagMenu}>
                    {m.menu}
                </span>
            ))}
            {child.textures.map((tex: MealTexture) => (
                <span key={tex.id} className={styles.TagTextureHabit}>
                    {tex.texture}
                </span>
            ))}
            {child.habits.map((e: MealHabit) => (
                <span key={e.id} className={styles.TagTextureHabit}>
                    {e.habit}
                </span>
            ))}
        </div>
    )
}

function renderChildTagDesktop(child: MealChild): JSX.Element {
    return (
        <>
            <TableCell className={styles.CellChild}>
                <div className={`${styles.CategoryTags} ${styles.CenterCellTags}`}>
                    {child.menus.map((m: MealMenu) => (
                        <span key={m.id} className={styles.TagMenu}>
                            {m.menu}
                        </span>
                    ))}
                </div>
            </TableCell>
            <TableCell className={styles.CellChild}>
                <div className={`${styles.CategoryTags} ${styles.CenterCellTags}`}>
                    {child.textures.map((tex: MealTexture) => (
                        <span key={tex.id} className={styles.TagTextureHabit}>
                            {tex.texture}
                        </span>
                    ))}
                </div>
            </TableCell>
            <TableCell className={styles.CellChild}>
                <div className={`${styles.CategoryTags} ${styles.CenterCellTags}`}>
                    {child.habits.map((e: MealHabit) => (
                        <span key={e.id} className={styles.TagTextureHabit}>
                            {e.habit}
                        </span>
                    ))}
                </div>
            </TableCell>
        </>
    )
}
